import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Badge } from "../ui/badge";
import {
  Upload, Download, ArrowLeft, ArrowRight, Check, AlertCircle,
  X, FileSpreadsheet, Search,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../../lib/api";

/**
 * Bulk invite upload — 4-step wizard.
 *
 *  1. Upload file (.csv, .xlsx, .xls). Parsed to a 2D array (rawSheet).
 *  2. Map columns — user picks which column is Phone (required), Name (opt),
 *     Product (opt). Header row is auto-detected but toggleable.
 *  3. Fix unmatched products — for every row whose product value we can't
 *     resolve, we show fuzzy suggestions + a full product dropdown + a
 *     "generic invite" fallback. Nothing gets created until every row is
 *     accepted (or explicitly marked generic / skipped).
 *  4. Create — parallel POST /api/admin/invites, progress bar, downloadable
 *     results CSV with input columns + invite_url + slug + error.
 *
 *  Invite URLs are emitted on the origin computed by InvitesSection's
 *  PUBLIC_ORIGIN helper (root offer.express in prod, current host in
 *  preview/dev, see `_resolveInviteOrigin` there).
 */

const CONCURRENCY = 5;
const MAX_ROWS = 500;

// ---------------------------------------------------------------------------
// Parsers
// ---------------------------------------------------------------------------

/** Parse either .csv (text) or .xlsx / .xls (binary) into a plain 2D array. */
async function parseSpreadsheet(file) {
  const name = (file?.name || "").toLowerCase();
  if (name.endsWith(".csv")) {
    const text = await file.text();
    return parseCSV(text);
  }
  // Everything else — let SheetJS decide. Handles .xlsx, .xls, .ods, tab-CSV.
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const first = wb.SheetNames[0];
  if (!first) return [];
  const sheet = wb.Sheets[first];
  // Coerce every cell to a plain string so downstream code doesn't hit
  // numeric phones getting truncated by scientific notation.
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: "" });
  return rows.map((r) => r.map((c) => (c == null ? "" : String(c).trim())));
}

/** Small CSV parser — handles quoted commas + escaped double quotes. */
function parseCSV(text) {
  const lines = String(text || "").replace(/\r/g, "").split("\n").filter((l) => l.trim() !== "");
  const rows = [];
  for (const line of lines) {
    const cells = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQ) {
        if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (ch === '"') inQ = false;
        else cur += ch;
      } else {
        if (ch === '"' && cur === "") inQ = true;
        else if (ch === ",") { cells.push(cur.trim()); cur = ""; }
        else cur += ch;
      }
    }
    cells.push(cur.trim());
    rows.push(cells);
  }
  return rows;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Strip any non-digit + normalise Indian phone prefixes. Returns "" if
 *  nothing usable. Same rules the backend uses server-side. */
function normalisePhone(raw) {
  let d = String(raw || "").replace(/\D/g, "");
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  else if (d.length === 13 && d.startsWith("091")) d = d.slice(3);
  else if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  return d;
}

/** Heuristic: does a row look like a header? True if any cell contains
 *  "phone"/"mobile"/"name"/"product"/"lender" (case-insensitive). */
function looksLikeHeader(row) {
  return row.some((c) =>
    /^(phone|mobile|name|product|lender|customer|number)$/i.test(String(c).trim())
    || /phone/i.test(String(c)) || /mobile/i.test(String(c))
    || /product/i.test(String(c)) || /lender/i.test(String(c))
  );
}

/** Try to guess column indexes from a header row. Returns null if none of
 *  phone/mobile match — caller shows the manual mapping UI in that case. */
function guessMapping(headerRow) {
  const lc = headerRow.map((c) => String(c).toLowerCase().trim());
  const find = (patterns) =>
    lc.findIndex((c) => patterns.some((p) => c.includes(p)));
  return {
    phone:   find(["phone", "mobile", "number"]),
    name:    find(["name"]),
    product: find(["product", "lender", "offer"]),
  };
}

/** Fuzzy score a needle against a single product. Higher = better. Returns
 *  0 when nothing matches so callers can filter. */
function scoreMatch(needle, product) {
  const s = needle.toLowerCase().trim();
  if (!s) return 0;
  const title = String(product.title || "").toLowerCase();
  const provider = String(product.provider || "").toLowerCase();
  let score = 0;
  if (title === s || provider === s) score += 1000;
  if (title.startsWith(s) || provider.startsWith(s)) score += 300;
  if (title.includes(s) || provider.includes(s)) score += 200;
  if (s.includes(title) && title) score += 150;
  if (provider && s.includes(provider)) score += 150;
  // Token overlap — cheap Jaccard over words ≥3 chars.
  const stok = new Set(s.split(/\s+/).filter((t) => t.length >= 3));
  const ptok = (title + " " + provider).split(/\s+/).filter((t) => t.length >= 3);
  for (const t of ptok) if (stok.has(t)) score += 40;
  return score;
}

/** Return the top N matches (with score) for the needle, ordered by score
 *  descending. */
function fuzzyMatches(needle, products, limit = 3) {
  return products
    .map((p) => ({ product: p, score: scoreMatch(needle, p) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function BulkUploadWizard({ open, onOpenChange, products, publicOrigin, onDone }) {
  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [rawSheet, setRawSheet] = useState([]);   // 2D array of cell strings
  const [hasHeader, setHasHeader] = useState(true);
  const [mapping, setMapping] = useState({ phone: -1, name: -1, product: -1 });
  const [rowFixes, setRowFixes] = useState({});    // rowIdx → { productId | "__GENERIC__" | "__SKIP__" }
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!open) {
      // Reset fully so the next launch starts clean.
      setStep("upload"); setFile(null); setRawSheet([]);
      setHasHeader(true); setMapping({ phone: -1, name: -1, product: -1 });
      setRowFixes({}); setProgress({ done: 0, total: 0 }); setResults(null);
    }
  }, [open]);

  // Data rows (skip the header when hasHeader).
  const dataRows = useMemo(
    () => (hasHeader ? rawSheet.slice(1) : rawSheet),
    [rawSheet, hasHeader]
  );

  // Row-level classification for the mapping preview + validation step.
  const rowInfos = useMemo(() => {
    if (mapping.phone < 0) return [];
    return dataRows.map((cells, idx) => {
      const phoneRaw = cells[mapping.phone] || "";
      const phone = normalisePhone(phoneRaw);
      const validPhone = phone.length === 10 && /^[6-9]/.test(phone);
      const name = mapping.name >= 0 ? (cells[mapping.name] || "").trim() : "";
      const productRaw = mapping.product >= 0 ? (cells[mapping.product] || "").trim() : "";
      const genericMarker = /^(any|generic|)$/i.test(productRaw);
      let productId = null;
      let matched = null;
      if (!genericMarker) {
        const hits = fuzzyMatches(productRaw, products, 1);
        if (hits.length && hits[0].score >= 200) {
          // High-confidence match — accept automatically.
          productId = hits[0].product.id;
          matched = hits[0].product;
        }
      }
      const needsFix = !validPhone
        ? false
        : (!genericMarker && productId === null && productRaw !== "");
      return { idx, phoneRaw, phone, validPhone, name, productRaw, productId, matched, needsFix, genericMarker };
    });
  }, [dataRows, mapping, products]);

  const stats = useMemo(() => {
    const total = rowInfos.length;
    const invalidPhone = rowInfos.filter((r) => !r.validPhone).length;
    const needFix = rowInfos.filter((r) => r.needsFix).length;
    const readyGeneric = rowInfos.filter((r) => r.validPhone && (r.genericMarker || (!r.productRaw && !r.matched))).length;
    const readyMatched = rowInfos.filter((r) => r.validPhone && r.matched).length;
    return { total, invalidPhone, needFix, readyGeneric, readyMatched };
  }, [rowInfos]);

  const onFileChosen = async (f) => {
    if (!f) return;
    setFile(f);
    try {
      const sheet = await parseSpreadsheet(f);
      if (sheet.length === 0) throw new Error("Empty file");
      setRawSheet(sheet);
      // Auto-detect header + auto-map columns.
      const isHeader = looksLikeHeader(sheet[0]);
      setHasHeader(isHeader);
      const guess = guessMapping(isHeader ? sheet[0] : sheet[0].map((_, i) => `Column ${i + 1}`));
      // Fall back to positional guessing when header didn't tell us.
      setMapping({
        phone:   guess.phone   >= 0 ? guess.phone   : 0,
        name:    guess.name    >= 0 ? guess.name    : (sheet[0].length > 1 ? 1 : -1),
        product: guess.product >= 0 ? guess.product : (sheet[0].length > 2 ? 2 : -1),
      });
      setStep("map");
    } catch (err) {
      toast.error(err.message || "Could not read file");
    }
  };

  const goValidate = () => {
    if (mapping.phone < 0) { toast.error("Pick which column contains the mobile number"); return; }
    if (rowInfos.length === 0) { toast.error("No data rows to process"); return; }
    if (rowInfos.length > MAX_ROWS) { toast.error(`Too many rows (${rowInfos.length}). Cap is ${MAX_ROWS}.`); return; }
    if (stats.needFix > 0) {
      setStep("fix");
    } else {
      submitAll();
    }
  };

  const submitAll = async () => {
    setStep("creating");
    setProgress({ done: 0, total: rowInfos.length });
    const output = new Array(rowInfos.length);
    let idx = 0;
    const worker = async () => {
      while (idx < rowInfos.length) {
        const my = idx++;
        const r = rowInfos[my];
        // Row-level fix overrides the auto-detected productId.
        const fix = rowFixes[r.idx];
        let productId = r.productId;
        if (fix === "__SKIP__") {
          output[my] = { row: r, invite_url: "", slug: "", error: "Skipped by admin" };
          setProgress((p) => ({ ...p, done: p.done + 1 }));
          continue;
        }
        if (fix === "__GENERIC__") productId = null;
        else if (fix) productId = fix;
        if (!r.validPhone) {
          output[my] = { row: r, invite_url: "", slug: "", error: "Invalid phone number" };
          setProgress((p) => ({ ...p, done: p.done + 1 }));
          continue;
        }
        try {
          const { data } = await api.post("/admin/invites", {
            customer_phone: r.phone,
            customer_name: r.name || undefined,
            product_id: productId || undefined,
          });
          output[my] = {
            row: r,
            invite_url: `${publicOrigin}/i/${data.slug}`,
            slug: data.slug,
            error: "",
          };
        } catch (err) {
          output[my] = { row: r, invite_url: "", slug: "", error: apiErrorMessage(err) || "Failed" };
        } finally {
          setProgress((p) => ({ ...p, done: p.done + 1 }));
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, rowInfos.length) }, worker));

    // Build results CSV — one row per input, includes generated link + error.
    const header = ["phone", "name", "product_input", "product_used", "invite_url", "slug", "error"];
    const escape = (v) => {
      const s = String(v == null ? "" : v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const body = output.map((o) => {
      const fix = rowFixes[o.row.idx];
      const productUsed = fix === "__GENERIC__" ? "any"
        : fix === "__SKIP__" ? "(skipped)"
        : fix ? (products.find((p) => p.id === fix)?.title || fix)
        : o.row.matched ? o.row.matched.title
        : o.row.genericMarker ? "any" : "";
      return [o.row.phoneRaw, o.row.name, o.row.productRaw, productUsed, o.invite_url, o.slug, o.error].map(escape).join(",");
    });
    const csv = [header.join(","), ...body].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const successes = output.filter((o) => o.invite_url).length;
    const failures = output.length - successes;
    setResults({
      successes, failures,
      csvBlobUrl: url,
      filename: `invites-${new Date().toISOString().slice(0, 10)}.csv`,
    });
    setStep("results");
    onDone?.();
    toast.success(`Created ${successes} invite${successes === 1 ? "" : "s"}${failures ? `, ${failures} failed` : ""}`);
  };

  const stepIndex = { upload: 0, map: 1, fix: 2, creating: 3, results: 3 }[step];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bulk-invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Bulk invite upload</DialogTitle>
          <StepIndicator step={stepIndex} />
        </DialogHeader>

        {step === "upload" && (
          <UploadStep onFileChosen={onFileChosen} products={products} />
        )}
        {step === "map" && (
          <MapStep
            rawSheet={rawSheet}
            hasHeader={hasHeader}
            setHasHeader={setHasHeader}
            mapping={mapping}
            setMapping={setMapping}
            rowInfos={rowInfos}
            stats={stats}
            onBack={() => setStep("upload")}
            onNext={goValidate}
          />
        )}
        {step === "fix" && (
          <FixStep
            rowInfos={rowInfos.filter((r) => r.needsFix)}
            rowFixes={rowFixes}
            setRowFixes={setRowFixes}
            products={products}
            onBack={() => setStep("map")}
            onNext={submitAll}
          />
        )}
        {step === "creating" && (
          <CreatingStep progress={progress} />
        )}
        {step === "results" && results && (
          <ResultsStep
            results={results}
            onRestart={() => { setStep("upload"); setFile(null); setRawSheet([]); setRowFixes({}); setResults(null); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Sub-views
// ---------------------------------------------------------------------------

function StepIndicator({ step }) {
  const labels = ["Upload", "Map columns", "Fix unmatched", "Create"];
  return (
    <div className="flex items-center gap-2 text-[11px] mt-1" data-testid="wizard-steps">
      {labels.map((l, i) => (
        <div key={l} className="flex items-center gap-2">
          <span
            className={
              "inline-flex items-center justify-center w-5 h-5 rounded-full font-semibold " +
              (i < step ? "bg-emerald-100 text-emerald-700"
                : i === step ? "bg-black text-white"
                : "bg-neutral-100 text-neutral-400")
            }
          >{i < step ? "✓" : i + 1}</span>
          <span className={i === step ? "font-semibold text-black" : "text-neutral-500"}>{l}</span>
          {i < labels.length - 1 && <span className="text-neutral-300">›</span>}
        </div>
      ))}
    </div>
  );
}

function UploadStep({ onFileChosen, products }) {
  const [dragging, setDragging] = useState(false);

  const downloadTemplate = () => {
    const banking = products.find((p) => p.type === "banking");
    const ecom = products.find((p) => p.type === "ecom");
    const template = [
      "phone,name,product",
      `9876543210,Sunil Verma,${banking ? banking.title : "Ram Fincorp Instant Loan"}`,
      `9123456789,,any`,
      `8888877777,Priya Iyer,${ecom ? ecom.title : "Flipkart"}`,
      `7654321098,Rohit Sharma,`,
    ].join("\n");
    const blob = new Blob([template], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "invites-template.csv";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    toast.success("Template downloaded");
  };

  return (
    <div className="space-y-4 mt-2">
      <label
        htmlFor="bulk-file"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragging(false);
          const f = e.dataTransfer.files?.[0]; if (f) onFileChosen(f);
        }}
        className={
          "block rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors " +
          (dragging ? "border-[#0055FF] bg-[#f0f4ff]" : "border-neutral-300 hover:border-neutral-400 bg-neutral-50")
        }
      >
        <FileSpreadsheet className="w-10 h-10 mx-auto text-neutral-400 mb-3" />
        <div className="font-semibold text-sm">Drop a file, or click to browse</div>
        <div className="text-[12px] text-neutral-500 mt-1">
          Accepts <code className="font-mono bg-white border border-neutral-200 rounded px-1">.csv</code>,{" "}
          <code className="font-mono bg-white border border-neutral-200 rounded px-1">.xlsx</code>,{" "}
          <code className="font-mono bg-white border border-neutral-200 rounded px-1">.xls</code> · max 500 rows
        </div>
        <input
          id="bulk-file"
          type="file"
          accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          className="hidden"
          data-testid="bulk-file-input"
          onChange={(e) => onFileChosen(e.target.files?.[0])}
        />
      </label>

      <div className="flex items-center justify-between text-[12px]">
        <span className="text-neutral-500">
          Not sure of the format? Grab a starter template — it uses your live product catalogue.
        </span>
        <button
          type="button"
          data-testid="bulk-template-download"
          onClick={downloadTemplate}
          className="text-[#0055FF] font-medium inline-flex items-center gap-1 hover:underline"
        >
          <Download className="w-3 h-3" /> template.csv
        </button>
      </div>
    </div>
  );
}

function MapStep({ rawSheet, hasHeader, setHasHeader, mapping, setMapping, rowInfos, stats, onBack, onNext }) {
  const previewRows = rawSheet.slice(0, hasHeader ? 6 : 5);
  const colCount = rawSheet[0]?.length || 0;
  const colOptions = Array.from({ length: colCount }, (_, i) => ({
    value: String(i),
    label: hasHeader ? `${rawSheet[0][i] || "?"} (col ${i + 1})` : `Column ${i + 1}`,
  }));

  return (
    <div className="space-y-4 mt-2">
      <div className="rounded-xl border border-neutral-200 overflow-hidden">
        <div className="flex items-center justify-between bg-neutral-50 px-3 py-2 border-b border-neutral-200">
          <span className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-semibold">Preview</span>
          <label className="inline-flex items-center gap-1.5 text-[12px] text-neutral-700 cursor-pointer">
            <input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} />
            First row is a header
          </label>
        </div>
        <div className="overflow-x-auto max-h-[220px]">
          <table className="w-full text-[12px]">
            <tbody>
              {previewRows.map((r, ri) => (
                <tr key={ri} className={hasHeader && ri === 0 ? "bg-[#f0f4ff] font-semibold" : "border-t border-neutral-100"}>
                  {r.map((c, ci) => (
                    <td key={ci} className="px-3 py-1.5 whitespace-nowrap max-w-[160px] overflow-hidden text-ellipsis">
                      {c || <span className="text-neutral-300">—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[
          { key: "phone",   label: "Mobile *",   required: true,  testid: "map-phone" },
          { key: "name",    label: "Name",       required: false, testid: "map-name" },
          { key: "product", label: "Product",    required: false, testid: "map-product" },
        ].map(({ key, label, required, testid }) => (
          <div key={key}>
            <label className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">{label}</label>
            <Select
              value={String(mapping[key])}
              onValueChange={(v) => setMapping((m) => ({ ...m, [key]: Number(v) }))}
            >
              <SelectTrigger data-testid={testid}><SelectValue placeholder="Not mapped" /></SelectTrigger>
              <SelectContent>
                {!required && <SelectItem value="-1">— not mapped —</SelectItem>}
                {colOptions.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>

      <div className="rounded-lg bg-neutral-50 border border-neutral-200 p-3 text-[12px] space-y-1" data-testid="map-stats">
        <div className="flex items-center justify-between">
          <span>Total rows</span><b>{stats.total}</b>
        </div>
        <div className="flex items-center justify-between text-emerald-700">
          <span>Auto-matched product</span><b>{stats.readyMatched}</b>
        </div>
        <div className="flex items-center justify-between text-neutral-700">
          <span>Generic (no product / “any”)</span><b>{stats.readyGeneric}</b>
        </div>
        <div className="flex items-center justify-between text-amber-700">
          <span>Needs product fix</span><b>{stats.needFix}</b>
        </div>
        {stats.invalidPhone > 0 && (
          <div className="flex items-center justify-between text-red-600">
            <span>Invalid phone (skipped)</span><b>{stats.invalidPhone}</b>
          </div>
        )}
      </div>

      <div className="flex justify-between gap-2">
        <Button variant="outline" onClick={onBack} data-testid="map-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button
          onClick={onNext}
          disabled={mapping.phone < 0 || stats.total === 0}
          data-testid="map-next"
          className="bg-[#0055FF] text-white hover:opacity-90"
        >
          {stats.needFix > 0
            ? <>Fix {stats.needFix} unmatched row{stats.needFix === 1 ? "" : "s"} <ArrowRight className="w-4 h-4 ml-1" /></>
            : <>Create {stats.total - stats.invalidPhone} invite{stats.total - stats.invalidPhone === 1 ? "" : "s"} <ArrowRight className="w-4 h-4 ml-1" /></>}
        </Button>
      </div>
    </div>
  );
}

function FixStep({ rowInfos, rowFixes, setRowFixes, products, onBack, onNext }) {
  const unresolvedCount = rowInfos.filter((r) => !rowFixes[r.idx]).length;

  return (
    <div className="space-y-3 mt-2" data-testid="fix-step">
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-[12px] text-amber-900 flex gap-2">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold">{rowInfos.length} row{rowInfos.length === 1 ? "" : "s"} need product fixing.</div>
          <div className="mt-0.5">Pick a suggestion, search the dropdown, or send them as a generic invite. Nothing gets created until every row is accepted.</div>
        </div>
      </div>

      <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
        {rowInfos.map((r) => (
          <UnmatchedRow
            key={r.idx}
            row={r}
            products={products}
            selected={rowFixes[r.idx]}
            onSelect={(val) => setRowFixes((s) => ({ ...s, [r.idx]: val }))}
          />
        ))}
      </div>

      <div className="flex justify-between gap-2">
        <Button variant="outline" onClick={onBack} data-testid="fix-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button
          onClick={onNext}
          disabled={unresolvedCount > 0}
          data-testid="fix-next"
          className="bg-[#0055FF] text-white hover:opacity-90"
        >
          {unresolvedCount > 0
            ? `Resolve ${unresolvedCount} more row${unresolvedCount === 1 ? "" : "s"}`
            : <>Create invites <ArrowRight className="w-4 h-4 ml-1" /></>}
        </Button>
      </div>
    </div>
  );
}

function UnmatchedRow({ row, products, selected, onSelect }) {
  const suggestions = useMemo(
    () => fuzzyMatches(row.productRaw, products, 3),
    [row.productRaw, products]
  );

  return (
    <div className="rounded-lg border border-neutral-200 p-3" data-testid={`fix-row-${row.idx}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[13px]">{row.phone}</div>
          <div className="text-[11px] text-neutral-500 truncate">
            {row.name || <span className="text-neutral-400">no name</span>}
            {" · "}
            you typed <span className="font-mono bg-amber-100 text-amber-900 px-1 rounded">{row.productRaw}</span>
          </div>
        </div>
        {selected && (
          <button
            className="text-[11px] text-neutral-500 hover:text-black underline underline-offset-2"
            onClick={() => onSelect(null)}
          >Clear</button>
        )}
      </div>

      {suggestions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {suggestions.map(({ product: p, score }) => {
            const active = selected === p.id;
            return (
              <button
                key={p.id}
                data-testid={`suggest-${row.idx}-${p.id}`}
                onClick={() => onSelect(p.id)}
                className={
                  "inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium border transition-colors " +
                  (active
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50")
                }
                title={`match score ${score}`}
              >
                <Search className="w-3 h-3" /> {p.title}
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-2 grid grid-cols-[1fr_auto_auto] gap-2 items-center">
        <Select
          value={selected && !["__GENERIC__", "__SKIP__"].includes(selected) ? selected : ""}
          onValueChange={(v) => onSelect(v)}
        >
          <SelectTrigger className="h-8 text-[12px]" data-testid={`fix-select-${row.idx}`}>
            <SelectValue placeholder="Or pick from full list…" />
          </SelectTrigger>
          <SelectContent>
            {(() => {
              const banking = products.filter((p) => p.type === "banking");
              const ecom = products.filter((p) => p.type === "ecom");
              const other = products.filter((p) => p.type !== "banking" && p.type !== "ecom");
              const grp = (label, arr) => arr.length ? (
                <div key={label}>
                  <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-semibold">{label}</div>
                  {arr.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                </div>
              ) : null;
              return [grp("Banking / Loans", banking), grp("E-commerce / Cashback", ecom), grp("Other", other)];
            })()}
          </SelectContent>
        </Select>
        <button
          onClick={() => onSelect("__GENERIC__")}
          data-testid={`fix-generic-${row.idx}`}
          className={
            "px-2 py-1.5 rounded-md text-[11px] font-medium border transition-colors whitespace-nowrap " +
            (selected === "__GENERIC__"
              ? "bg-black text-white border-black"
              : "bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50")
          }
        >Send as generic</button>
        <button
          onClick={() => onSelect("__SKIP__")}
          data-testid={`fix-skip-${row.idx}`}
          className={
            "px-2 py-1.5 rounded-md text-[11px] font-medium border transition-colors whitespace-nowrap " +
            (selected === "__SKIP__"
              ? "bg-red-600 text-white border-red-600"
              : "bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50")
          }
        >Skip row</button>
      </div>
    </div>
  );
}

function CreatingStep({ progress }) {
  const pct = progress.total ? (progress.done / progress.total) * 100 : 0;
  return (
    <div className="space-y-3 py-6" data-testid="creating-step">
      <div className="flex items-center justify-between text-[13px]">
        <span className="text-neutral-600">Creating invites…</span>
        <span className="font-mono">{progress.done} / {progress.total}</span>
      </div>
      <div className="w-full h-2 bg-neutral-200 rounded-full overflow-hidden">
        <div className="h-full bg-[#0055FF] transition-[width] duration-100" style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[11px] text-neutral-500 text-center">
        Running {CONCURRENCY} concurrent creates. This should complete in under a minute for 100 rows.
      </p>
    </div>
  );
}

function ResultsStep({ results, onRestart }) {
  return (
    <div className="space-y-3 mt-2" data-testid="results-step">
      <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-emerald-100 grid place-items-center shrink-0">
          <Check className="w-5 h-5 text-emerald-600" />
        </div>
        <div className="flex-1">
          <div className="font-semibold">
            {results.successes} invite{results.successes === 1 ? "" : "s"} created
            {results.failures > 0 && <span className="text-red-600"> · {results.failures} failed</span>}
          </div>
          <p className="text-[12px] text-neutral-600 mt-1">
            Download the results CSV — every input row plus the generated link, the product we actually used, and any error.
          </p>
        </div>
      </div>
      <a
        href={results.csvBlobUrl}
        download={results.filename}
        data-testid="bulk-download"
        className="inline-flex items-center justify-center gap-2 w-full h-11 rounded-lg bg-[#0055FF] text-white text-sm font-semibold hover:opacity-90"
      >
        <Download className="w-4 h-4" /> Download results ({results.filename})
      </a>
      <button
        onClick={onRestart}
        className="w-full text-xs text-neutral-500 hover:text-neutral-900 underline underline-offset-4"
      >
        Upload another batch
      </button>
    </div>
  );
}
